import { useState, type ReactElement } from 'react';
import EmberFlame from '../../../../../shared-web/EmberFlame';
import { trackFailed } from '../../../../../shared-web/track';
import { apiFailureReason, emberApi, type AgeCheckAnswer } from '../api';
import { MinorNotice } from './MinorNotice';
import './KnockCard.css';

/**
 * The age check's question (edge age-check.js, 2026-10-27 founder): Ember read an age under 18 in what the person said,
 * after they had ticked 18+. Drawn under Ember's turn in the thread while it stands, and on /age (the link the text
 * door is sent). "No" asks once more before it is sent, because it deletes what they shared. `onAnswered` gets the
 * answer once the edge has it; on `under_18` the card gives way to the minor notice (2026-11-02 founder).
 */
export function AgeCheckQuestion({ onAnswered }: { onAnswered?: (answer: 'adult' | 'under_18', reply: AgeCheckAnswer) => void }): ReactElement {
  const [step, setStep] = useState<'ask' | 'sure' | 'closed'>('ask');
  const [busy, setBusy] = useState(false);

  async function send(answer: 'adult' | 'under_18'): Promise<void> {
    setBusy(true);
    try {
      const r = await emberApi.answerAgeCheck(answer);
      if (answer === 'under_18') setStep('closed');
      onAnswered?.(answer, r);
    } catch (err) {
      trackFailed('age_check_answer', apiFailureReason(err));
    } finally {
      setBusy(false);
    }
  }

  if (step === 'closed') return <MinorNotice />;
  return (
    <div className="knock-card" role="group" aria-label="Confirm your age">
      <div className="knock-head">
        <span className="knock-mark" aria-hidden><EmberFlame className="knock-flame" /></span>
        <h2 className="knock-title">{step === 'ask' ? 'Are you 18 or older?' : 'Just to be sure'}</h2>
      </div>
      {step === 'ask' ? (
        <>
          <p className="knock-lede">Ember is only for adults. Once you answer, we can carry on.</p>
          <div className="knock-actions">
            <button type="button" className="ember-btn ember-btn-primary" disabled={busy} data-log="age_check_adult" onClick={() => { void send('adult'); }}>
              {busy ? 'One moment…' : "Yes, I'm 18 or older"}
            </button>
            <button type="button" className="ember-btn ember-btn-secondary" disabled={busy} data-log="age_check_under" onClick={() => setStep('sure')}>
              No, I'm under 18
            </button>
          </div>
        </>
      ) : (
        <>
          <p className="knock-lede">If you're under 18, we'll close this and delete everything you've shared here. This can't be undone.</p>
          <div className="knock-actions">
            <button type="button" className="ember-btn ember-btn-primary" disabled={busy} data-log="age_check_under_confirm" onClick={() => { void send('under_18'); }}>
              Yes, I'm under 18
            </button>
            <button type="button" className="ember-btn ember-btn-secondary" disabled={busy} data-log="age_check_back" onClick={() => setStep('ask')}>
              Go back
            </button>
          </div>
        </>
      )}
    </div>
  );
}
